import { api } from './client';

/**
 * Type definitions and API wrappers for courses-service.
 * Endpoints — see services/courses-service/internal/handler/course_handler.go.
 */

// ----- Enums (mirror backend constants) -----

export type MarathonStatus = 'draft' | 'published' | 'archived';
export type AccessMode = 'open' | 'sequential' | 'with_review';

/**
 * Кто ставит балл за пройденный урок (разбаловка, 10.09.2026).
 *
 * ⚠️ НЕ ПУТАТЬ С AccessMode. Тот отвечает на вопрос «когда откроется
 * СЛЕДУЮЩИЙ урок» и управляет доступом; этот — «откуда берётся балл».
 * Соседство обманчиво: пересечения между ними нет.
 */
export type GradingMode = 'curator' | 'auto';

/**
 * Категория доступа курса (решение F5, 10.09.2026; договор `mirror-v2.md` §1).
 *
 * ⚠️ НЕЗАВИСИМА ОТ ПОДТИПА. На один подтип приходится до трёх курсов — по
 * одному на категорию; `null` значит «не указана», и на 20.09.2026 она не
 * указана ни у одного курса на проде.
 */
export type AccessCategory = 'one_to_group' | 'one_to_one' | 'self';

/**
 * Тело `PUT /admin/marathons/{id}/classification`.
 *
 * ⚠️ ОТСУТСТВИЕ КЛЮЧА И `null` — РАЗНОЕ. Ключа нет — поле не меняется; `null` —
 * поле снимается. Поэтому оба поля необязательные, а не `T | null` со значением
 * по умолчанию: `{ subtype: null }`, посланное «на всякий случай», сняло бы
 * подтип и вывело курс из потока.
 */
export type ClassificationBody = {
  subtype?: string | null;
  access_category?: AccessCategory | null;
  /** Обязателен, если в теле есть `subtype`. Сверяется буква в букву. */
  confirm_title?: string;
};
export type RatingScope = 'all' | 'top_5' | 'top_10';
export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
export type Language = 'KZ' | 'RU' | 'EN';

// ----- Models -----

export type Marathon = {
  id: string;
  title: string;
  slug: string;
  cover_url?: string;
  background_url?: string;
  category?: string;
  language: Language;
  level_start: CEFRLevel;
  level_end: CEFRLevel;
  access_mode: AccessMode;
  show_rating: boolean;
  rating_scope: RatingScope;
  show_student_rank: boolean;
  // Приватный марафон не показывается в публичном каталоге; ученики попадают
  // в него только через ручное зачисление админом.
  is_private?: boolean;
  /** Бесплатный марафон: открыт каждому вошедшему без записи и тарифа. */
  is_free?: boolean;
  /** Порядок в каталоге: меньше — выше; при равных — новее выше (админка, техдолг 321). */
  display_order?: number;
  /** Новое имя `is_free` в ответе ученика (договор student-api.md §4). Читать через `isOpenCourse`. */
  is_open?: boolean;
  /**
   * Курс для гостевой главной приложения (D58). Сервер добавит поле позже; пока его нет —
   * гостю показываются бесплатные (`lib/guestMode.ts`, договор docs/CONTRACTS/guest-mode.md).
   */
  is_guest?: boolean;
  /**
   * Когда начинается обучение. Пусто или прошедшая дата — идёт.
   *
   * ⚠️ ТАРИФ И СТАРТ — РАЗНЫЕ ВЕЩИ. Тарифы школа раздаёт заранее, за недели до
   * первого урока. До этой даты ученик с тарифом не получает НИ ОДНОЙ платной
   * возможности и видит только бесплатные курсы.
   */
  starts_at?: string | null;
  status: MarathonStatus;
  modules?: Module[];
  // Tariffs bound to this marathon, returned inline by the admin detail getter.
  tariffs?: TariffPlan[];
  created_at?: string;
  updated_at?: string;
  // computed (admin lists)
  student_count?: number;
  lesson_count?: number;
  /**
   * Поток и уровень курса (`sept26-preupper`); `null` — курс вне потоков.
   *
   * ⚠️ ТОЛЬКО ЧТЕНИЕ. Общий `PUT /admin/marathons/{id}` подтип не пишет, даже
   * если его прислать: пишет одна ручка классификации, и только владельцу
   * (`model/course_models.go:31`, договор `mirror-v2.md` §1).
   */
  subtype?: string | null;
  /** Категория доступа; только чтение, по той же причине, что и подтип. */
  access_category?: AccessCategory | null;
  /** Образец курса: из него создаются рабочие курсы копией. Ученикам не виден. */
  is_template?: boolean;
  /** Из какого образца создан курс. Пусто — курс сам по себе. */
  source_template_id?: string | null;
};

export type Module = {
  id: string;
  marathon_id: string;
  title: string;
  order_index: number;
  lessons?: Lesson[];
};

export type LessonAccessStatus = 'passed' | 'available' | 'locked';

export type Lesson = {
  id: string;
  module_id: string;
  title: string;
  cover_url?: string;
  points: number;
  status: 'draft' | 'published';
  order_index: number;
  sections?: Section[];
  // Computed on read by courses-service.
  marathon_id?: string; // resolved via module — needed to start progress
  is_available?: boolean;
  access_status?: LessonAccessStatus; // passed | available | locked
};

export type Section = {
  id: string;
  lesson_id: string;
  title: string;
  deadline?: string;
  order_index: number;
  is_homework?: boolean;
  blocks?: Block[];
};

// 26+ block types per ТЗ §3.2. JSON content varies per type.
export type BlockType =
  | 'image' | 'image_carousel' | 'gif'
  | 'video' | 'audio' | 'audio_record'
  | 'word_drag_to_gap' | 'word_input_gap' | 'word_form_select'
  | 'word_drag_to_image' | 'word_input_image' | 'word_form_image'
  | 'words_to_pictures'
  | 'gap_fill' | 'table_fill'
  | 'quiz_no_timer' | 'quiz_with_timer'
  | 'true_false_ng'
  | 'sentence_order' | 'column_sort' | 'paragraph_order' | 'anagram' | 'matching'
  | 'article' | 'text' | 'essay' | 'open_items'
  | 'word_card_set' | 'callout' | 'link' | 'divider'
  | 'embed' | 'highlight' | 'pdf';

/**
 * Что умеет плеер этого клиента, для сервера (`?blocks=` у урока ученика).
 * 2 — банк `gap_fill` с одинаковыми словами; 3 — одно слово в двух парах
 * `matching`; 4 — тип `table_fill` «Дополни таблицу»; 5 — тип `open_items` «Ответ
 * по пунктам» (все 13.10.2026). Без параметра сервер считает клиента приложением
 * 1.1.1 — договор `docs/CONTRACTS/block-types-1.1.2.md`.
 */
export const STUDENT_BLOCKS_VERSION = 5;

export type Block = {
  id: string;
  section_id: string;
  type: BlockType;
  content: Record<string, unknown>;
  order_index: number;
};

export type TariffPlan = {
  id: string;
  // null/undefined => platform-wide tariff (TZ 3.9).
  marathon_id?: string | null;
  title: string;
  description?: string;
  price: number;
  discount_price?: number;
  currency: string;
  duration_days: number;
  features: string;
  /** Ключи возможностей тарифа — то, что щёлкают тумблерами. */
  feature_keys?: string[] | null;
  is_active: boolean;
  display_order?: number;
};

// ----- Endpoints -----

export type MarathonListParams = {
  offset?: number;
  limit?: number;
  status?: MarathonStatus;
  language?: Language;
  level?: CEFRLevel;
  category?: string;
  q?: string;
  /** 'true' — только образцы, 'false' — только рабочие курсы. */
  template?: 'true' | 'false';
};

// Backend stores Block.content as a raw JSON string (jsonb column marshalled
// as text). The frontend Block type expects a parsed object, so normalize any
// nested blocks coming back inside lessons / sections / marathons.
function parseBlockContent(block: Block): Block {
  if (block && typeof block.content === 'string') {
    try {
      return { ...block, content: JSON.parse(block.content as unknown as string) };
    } catch {
      return { ...block, content: {} };
    }
  }
  return block;
}

function normalizeSection(section: Section): Section {
  if (section?.blocks) {
    return { ...section, blocks: section.blocks.map(parseBlockContent) };
  }
  return section;
}

function normalizeLesson(lesson: Lesson): Lesson {
  if (lesson?.sections) {
    return { ...lesson, sections: lesson.sections.map(normalizeSection) };
  }
  return lesson;
}

function normalizeMarathon(marathon: Marathon): Marathon {
  if (marathon?.modules) {
    return {
      ...marathon,
      modules: marathon.modules.map((m) => ({
        ...m,
        lessons: m.lessons?.map(normalizeLesson),
      })),
    };
  }
  return marathon;
}

export const coursesApi = {
  // Public catalog
  async listPublishedMarathons(params: MarathonListParams = {}) {
    const { data } = await api.get('/courses/catalog/marathons', { params });
    return data as { items: Marathon[]; total: number };
  },
  async getMarathonBySlug(slug: string) {
    const { data } = await api.get(`/courses/catalog/marathons/${slug}`);
    return normalizeMarathon(data as Marathon);
  },

  // Admin
  async listAllMarathons(params: MarathonListParams = {}) {
    const { data } = await api.get('/admin/marathons', { params });
    return data as { items: Marathon[]; total: number };
  },
  /**
   * Образцы курсов («материалы»).
   *
   * Образец — полноценный курс, из которого рабочие курсы создаются копией.
   * В коде это «template», а не «material»: в платформе уже есть раздел
   * «Материалы» — видео и статьи для учеников, и два разных «материала»
   * перепутают наверняка.
   */
  /**
   * Снять с марафона образец — самостоятельную копию.
   *
   * Сам марафон не меняется: ученики и их работы остаются на месте. Возвращает
   * опознаватель нового образца.
   */
  async makeTemplate(id: string) {
    const { data } = await api.post(`/admin/marathons/${id}/make-template`);
    return data as { id: string };
  },
  async unmakeTemplate(id: string) {
    await api.post(`/admin/marathons/${id}/unmake-template`);
  },
  /**
   * Привязать УЖЕ СУЩЕСТВУЮЩИЙ курс к образцу.
   *
   * ⚠️ Нужно потому, что связь ставилась только при создании курса ИЗ образца.
   * Курсы, заведённые копированием, раскатку правок не получали, а заводить их
   * заново значило потерять проставленные даты старта и тарифы.
   *
   * ⚠️ Связывается по совпадению места и названия. Любое расхождение —
   * отказ целиком: неверная связь тише и дороже отказа, правка образца уехала
   * бы не в тот урок.
   */
  async bindTemplate(marathonId: string, templateId: string) {
    const { data } = await api.post(`/admin/marathons/${marathonId}/bind-template`, {
      template_id: templateId,
    });
    return data as { modules: number; lessons: number; sections: number; blocks: number };
  },
  /** Создать курс полной копией образца. Копия приходит черновиком. */
  async createFromTemplate(templateId: string, title: string, slug: string) {
    const { data } = await api.post(`/admin/marathons/${templateId}/create-from-template`, { title, slug });
    return data as { id: string };
  },
  /**
   * Применить правки образца к созданным из него курсам.
   *
   * Действие ручное: методист правит образец сколько нужно и сам решает, когда
   * это увидят курсы. Правленное куратором в курсе остаётся как есть.
   */
  async syncFromTemplate(id: string) {
    const { data } = await api.post(`/admin/marathons/${id}/sync-template`);
    return data as { updated: number; kept: number; added: number; hidden: number; courses: number };
  },
  /** Сколько курсов создано из образца — показываем перед правкой. */
  async templateUsage(id: string) {
    const { data } = await api.get(`/admin/marathons/${id}/template-usage`);
    return (data as { courses: number }).courses;
  },
  async getMarathon(id: string) {
    const { data } = await api.get(`/admin/marathons/${id}`);
    return normalizeMarathon(data as Marathon);
  },
  async createMarathon(payload: Partial<Marathon>) {
    const { data } = await api.post('/admin/marathons', payload);
    return data as Marathon;
  },
  async updateMarathon(id: string, payload: Partial<Marathon>) {
    const { data } = await api.put(`/admin/marathons/${id}`, payload);
    return data as Marathon;
  },
  async duplicateMarathon(id: string) {
    const { data } = await api.post(`/admin/marathons/${id}/duplicate`);
    return data as Marathon;
  },
  async publishMarathon(id: string) {
    await api.post(`/admin/marathons/${id}/publish`);
  },
  async archiveMarathon(id: string) {
    await api.post(`/admin/marathons/${id}/archive`);
  },

  // Modules / Lessons / Sections / Blocks
  async createModule(payload: { marathon_id: string; title: string; order_index?: number }) {
    const { data } = await api.post('/admin/modules', payload);
    return data as Module;
  },
  async createLesson(payload: { module_id: string; title: string; points?: number; order_index?: number }) {
    const { data } = await api.post('/admin/lessons', payload);
    return data as Lesson;
  },
  async createSection(payload: { lesson_id: string; title: string; deadline?: string; order_index?: number; is_homework?: boolean }) {
    const { data } = await api.post('/admin/sections', payload);
    return data as Section;
  },
  async createBlock(payload: { section_id: string; type: BlockType; content: Record<string, unknown>; order_index?: number }) {
    // Backend persists content as a JSON string; send it stringified and
    // re-parse the echoed block so callers get an object back.
    const { data } = await api.post('/admin/blocks', {
      ...payload,
      content: JSON.stringify(payload.content),
    });
    return parseBlockContent(data as Block);
  },
  async reorderBlocks(payload: { section_id: string; ordered_ids: string[] }) {
    // Backend ReorderBlocks binds a required `ids` array (section_id is unused).
    await api.put('/admin/blocks/reorder', { ids: payload.ordered_ids });
  },
  // AI-draft a set of lesson blocks from a topic. Backend returns block drafts
  // with content as a JSON string; parse each into an object for the builder.
  async generateLesson(payload: { topic: string; level?: string; language?: string; count?: number }) {
    const { data } = await api.post('/admin/lessons/generate', payload);
    const blocks = (data as { blocks?: { type: BlockType; content: string }[] }).blocks ?? [];
    return blocks.map((b) => {
      let content: Record<string, unknown> = {};
      try {
        content = JSON.parse(b.content) as Record<string, unknown>;
      } catch {
        content = {};
      }
      return { type: b.type, content };
    });
  },

  async deleteMarathon(id: string) {
    await api.delete(`/admin/marathons/${id}`);
  },
  async updateModule(id: string, payload: { title?: string; order_index?: number }) {
    const { data } = await api.put(`/admin/modules/${id}`, payload);
    return data as Module;
  },
  async deleteModule(id: string) {
    await api.delete(`/admin/modules/${id}`);
  },
  async updateLesson(
    id: string,
    payload: { title?: string; cover_url?: string; points?: number; status?: string; order_index?: number },
  ) {
    const { data } = await api.put(`/admin/lessons/${id}`, payload);
    return data as Lesson;
  },
  async deleteLesson(id: string) {
    await api.delete(`/admin/lessons/${id}`);
  },
  async reorderLessons(payload: { module_id: string; ordered_ids: string[] }) {
    await api.put('/admin/lessons/reorder', payload);
  },
  /** Урок на одно место по плоскому списку курса; `moved: false` — урок крайний. */
  async stepLesson(id: string, direction: 'up' | 'down') {
    const { data } = await api.post(`/admin/lessons/${id}/step`, { direction });
    return data as { moved: boolean };
  },
  async updateSection(
    id: string,
    payload: { title?: string; deadline?: string; order_index?: number; is_homework?: boolean },
  ) {
    const { data } = await api.put(`/admin/sections/${id}`, payload);
    return data as Section;
  },
  async deleteSection(id: string) {
    await api.delete(`/admin/sections/${id}`);
  },
  async updateBlock(
    id: string,
    payload: { type?: BlockType; content?: Record<string, unknown>; order_index?: number },
  ) {
    const { data } = await api.put(`/admin/blocks/${id}`, payload);
    return data as Block;
  },
  async deleteBlock(id: string) {
    await api.delete(`/admin/blocks/${id}`);
  },
  async getFullLesson(id: string) {
    const { data } = await api.get(`/admin/lessons/${id}`);
    return normalizeLesson(data as Lesson);
  },

  /**
   * Копировать и переместить блок, раздел, урок (договор `constructor-v2.md` §2).
   *
   * Узел встаёт В КОНЕЦ выбранного родителя. Копия получает новые id, перемещение
   * сохраняет прежние. Ответ того же вида, что у `getFullLesson`: `content` —
   * строка JSON, поэтому прогоняем через те же нормализаторы.
   *
   * ⚠️ ПУТИ — БУКВАЛЬНЫЕ, ШЕСТЬ ШТУК. Собрать их из переменной (`/admin/${kind}/…`)
   * нельзя: `adminRoutesInGateway.test.ts` сверяет каждый вызов с картой шлюза и
   * маршрутами службы и путь из переменной проверить не может.
   */
  async copyBlock(id: string, sectionId: string) {
    const { data } = await api.post(`/admin/blocks/${id}/copy`, { section_id: sectionId });
    return parseBlockContent(data as Block);
  },
  async moveBlock(id: string, sectionId: string) {
    const { data } = await api.post(`/admin/blocks/${id}/move`, { section_id: sectionId });
    return parseBlockContent(data as Block);
  },
  async copySection(id: string, lessonId: string) {
    const { data } = await api.post(`/admin/sections/${id}/copy`, { lesson_id: lessonId });
    return normalizeSection(data as Section);
  },
  async moveSection(id: string, lessonId: string) {
    const { data } = await api.post(`/admin/sections/${id}/move`, { lesson_id: lessonId });
    return normalizeSection(data as Section);
  },
  async copyLesson(id: string, moduleId: string) {
    const { data } = await api.post(`/admin/lessons/${id}/copy`, { module_id: moduleId });
    return normalizeLesson(data as Lesson);
  },
  async moveLesson(id: string, moduleId: string) {
    const { data } = await api.post(`/admin/lessons/${id}/move`, { module_id: moduleId });
    return normalizeLesson(data as Lesson);
  },

  // Student lesson player — sequential-access guarded endpoint in
  // courses-service (/courses/content/lessons/:id). Returns the lesson with
  // its sections + blocks; block.content arrives as a JSON string so we
  // normalize it the same way as the admin getter.
  //
  // ⚠️ `blocks` обязателен: без него сервер считает клиента старым (приложение
  // 1.1.1, вкладка, открытая до выката) и подменяет задания, которые старый
  // плеер покажет сломанными, — см. STUDENT_BLOCKS_VERSION.
  async getStudentLesson(id: string) {
    const { data } = await api.get(`/courses/content/lessons/${id}`, { params: { blocks: STUDENT_BLOCKS_VERSION } });
    return normalizeLesson(data as Lesson);
  },

  // Authenticated marathon tree with per-lesson access status
  // (passed / available / locked) for the enrolled student — TZ 3.2.
  async getStudentMarathonTree(marathonId: string) {
    const { data } = await api.get(`/courses/content/marathons/${marathonId}/tree`);
    return normalizeMarathon(data as Marathon);
  },

  // ----- Тарифы (admin) -----
  // ⚠️ Раздел «Тарифы» в админке убран 13.10.2026 (решение владельца): создание, правка, архив и мини-курсы тарифа
  // больше не зовутся и удалены отсюда. Список остаётся — тариф ученика выбирают в его карточке, форме и массовой
  // загрузке (на сайте; в приложении этих блоков нет). Тарифы в базе и права по ним не тронуты.
  async listTariffs() {
    const { data } = await api.get('/admin/tariffs');
    return ((data as { items?: TariffPlan[] }).items ?? (data as TariffPlan[]) ?? []) as TariffPlan[];
  },
  /**
   * Настройки курса — то, что раньше настраивали в тарифе.
   *
   * ⚠️ Переезд 07.09.2026: права и состав дополнительных курсов переехали с
   * тарифа на сам курс. Тарифы остались в базе нетронутыми — возврат возможен
   * переменной окружения на сервере.
   */
  async getMarathonSettings(marathonId: string) {
    const { data } = await api.get(`/admin/marathons/${marathonId}/settings`);
    return data as {
      feature_keys: string[];
      extras: TariffExtra[];
      /** Подтип курса; пустая строка — курс вне потоков. */
      subtype: string;
      /** Категория доступа; `null` — не указана. */
      access_category: AccessCategory | null;
      /**
       * Вправе ли спросивший менять подтип — он в `courses.subtype_owners`.
       *
       * ⚠️ ПОЛЕ ПОДТИПА ПОКАЗЫВАТЬ ТОЛЬКО ПРИ `true` (договор §1). Подтип
       * втягивает курс в зеркало, и админу без права сервер ответит 403
       * `subtype_owner_only` — показать ему поле значит пообещать несбыточное.
       */
      subtype_editable: boolean;
      /** Кто ставит балл за урок: 'auto' — система, 'curator' — куратор руками. */
      grading_mode: GradingMode;
      /**
       * Бесплатный ли курс. Нужен интерфейсу, а не настройке: у мини-курса
       * тумблер не показывается вовсе — режим на него не действует, балл берётся
       * из самого урока. Показать переключатель, который ничего не меняет, —
       * значит соврать администратору.
       */
      is_free: boolean;
      /**
       * Навыки ИИ-заданий, выключенные ученикам этого курса (docs/CONTRACTS/ai-skills.md).
       * Пусто — курс ничего не запрещает. Старый сервер поля не шлёт — читать через `?? []`.
       */
      ai_skills_off?: string[];
    };
  },
  /** Выключенные навыки ИИ-заданий курса — список целиком (docs/CONTRACTS/ai-skills.md §4). */
  async setMarathonAISkills(marathonId: string, off: string[]) {
    await api.put(`/admin/marathons/${marathonId}/ai-skills`, { off });
  },
  /**
   * Переключить, кто ставит балл за урок.
   *
   * Отдельная ручка, а не поле в общем PUT /admin/marathons/:id — по той же
   * причине, что права и состав: их правят в разные моменты, и сохранение
   * одного не должно молча переписывать другое.
   */
  async setMarathonGrading(marathonId: string, mode: GradingMode) {
    await api.put(`/admin/marathons/${marathonId}/grading`, { grading_mode: mode });
  },
  async setMarathonRights(marathonId: string, featureKeys: string[]) {
    await api.put(`/admin/marathons/${marathonId}/rights`, { feature_keys: featureKeys });
  },
  /**
   * Подтип и категория доступа курса (договор `mirror-v2.md` §2).
   *
   * ⚠️ ТЕЛО СОБИРАЕТ ВЫЗЫВАЮЩИЙ, И ОТСУТСТВИЕ КЛЮЧА ≠ `null`: ключа нет —
   * поле не меняется, `null` — поле снимается. Сборка — в
   * `lib/marathonClassification.ts`, чтобы это правило проверялось.
   *
   * ⚠️ `confirm_title` ОБЯЗАТЕЛЕН ВМЕСТЕ С `subtype` и сверяется буква в
   * букву: названия курсов на проде кривые (двойной пробел, обратный
   * апостроф), поэтому подставлять его надо ровно тем, что пришло с сервера.
   */
  async setMarathonClassification(marathonId: string, body: ClassificationBody) {
    const { data } = await api.put(`/admin/marathons/${marathonId}/classification`, body);
    return (data ?? {}) as { subtype: string | null; access_category: AccessCategory | null };
  },
  /** Состав задаётся целиком: «сохранить» означает «стало ровно так». */
  async setMarathonExtras(marathonId: string, marathonIds: string[]) {
    await api.put(`/admin/marathons/${marathonId}/extras`, { marathon_ids: marathonIds });
  },
};

/** Дополнительный курс в настройках курса (`extras`); прежде — мини-курс в составе тарифа. */
export type TariffExtra = {
  marathon_id: string;
  title: string;
  cover_url: string;
  display_order: number;
};
